
import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, Building2, FolderOpen, Folder, Sparkles } from "lucide-react";
import { Breadcrumb, createProjectBreadcrumb } from "@/components/ui/breadcrumb";
import ProjectSelector from "./ProjectSelector";
import CreateProjectDialog from "@/components/Auth/Projects/CreateProjectDialog";
import { toast } from "sonner";
import { useProjects } from "@/components/Auth/Projects/useProjects";

interface PageHeaderProps {
  projectId: string | null;
  projectName?: string;
  selectedFolderId?: string | null;
  selectedFolderName?: string;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  folderTreeKey?: number;
  onCreateFolder?: () => void;
  onProjectChange: (projectId: string) => Promise<void>;
  onCreateProject?: (name: string) => Promise<void>;
  onNavigateToProject?: () => void;
  onNavigateToFolder?: () => void;
}

const PageHeader: React.FC<PageHeaderProps> = ({ 
  projectId,
  projectName,
  selectedFolderId,
  selectedFolderName,
  onCreateFolder,
  onProjectChange,
  onCreateProject,
  onNavigateToProject,
  onNavigateToFolder
}) => {
  const [isCreateProjectDialogOpen, setIsCreateProjectDialogOpen] = useState(false);
  const { projects } = useProjects();

  // Get the current project details
  const currentProject = projects.find(p => p.id === projectId);
  const displayProjectName = projectName || currentProject?.name;

  const handleCreateProject = async (name: string) => {
    if (onCreateProject) {
      try {
        await onCreateProject(name);
        setIsCreateProjectDialogOpen(false);
        toast.success("Projekt skapat framgångsrikt!");
      } catch (error) {
        console.error("Failed to create project:", error);
        toast.error("Kunde inte skapa projekt");
      }
    }
  };

  const handleProjectChange = async (newProjectId: string) => {
    try {
      await onProjectChange(newProjectId);
      toast.success("Projekt bytt framgångsrikt!");
    } catch (error) {
      console.error("Failed to change project:", error);
      toast.error("Kunde inte byta projekt");
    }
  };

  // Create breadcrumb items
  const breadcrumbItems = createProjectBreadcrumb(
    displayProjectName || "Oprojekt",
    selectedFolderId ? selectedFolderName : undefined,
    onNavigateToProject,
    onNavigateToFolder
  );

  return (
    <div className="space-y-4">
      {/* Breadcrumb Navigation */}
      {projectId && (
        <div className="glassmorphism bg-white/10 backdrop-blur-xl rounded-lg border border-white/20 shadow-sm px-4 py-3">
          <Breadcrumb items={breadcrumbItems} />
        </div>
      )}

      {/* Streamlined Header */}
      <div className="glassmorphism bg-white/10 backdrop-blur-2xl rounded-xl border border-white/20 shadow-lg overflow-hidden">
        <CardContent className={selectedFolderId ? "p-4 sm:p-5" : "p-6"}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            {/* Left side - Context info */}
            <div className="flex items-center gap-3">
              <div className="p-2 bg-terracotta/20 rounded-lg">
                <Building2 className="h-5 w-5 text-terracotta" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-marine">
                  {selectedFolderId ? selectedFolderName : 
                   displayProjectName ? displayProjectName : "Projekthantering"}
                </h1>
                <p className="text-sm text-on-hero/80">
                  {selectedFolderId ? "Mappinnehåll och bedömningar" :
                   !projectId ? "Välj eller skapa ett projekt" : 
                   "Projektöversikt"}
                </p>
              </div>
            </div>
            
            {/* Right side - Action buttons */}
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Button
                onClick={() => setIsCreateProjectDialogOpen(true)}
                variant="outline"
                size="sm"
                className="border-white/30 hover:border-white/50 text-marine-900 hover:bg-white/10"
              >
                <Plus className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Nytt projekt</span>
              </Button>

              <ProjectSelector 
                projectId={projectId} 
                onProjectChange={handleProjectChange}
              />
              
              {projectId && onCreateFolder && !selectedFolderId && (
                <Button
                  onClick={onCreateFolder}
                  className="bg-terracotta hover:bg-terracotta/90 text-white shadow-md"
                  size="sm"
                >
                  <Folder className="h-4 w-4 mr-2 text-foreground" />
                  <span className="text-foreground">Ny mapp</span>
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </div>


      {/* Create Project Dialog */}
      <CreateProjectDialog
        isOpen={isCreateProjectDialogOpen}
        onOpenChange={setIsCreateProjectDialogOpen}
        onCreateProject={handleCreateProject}
      />
    </div>
  );
};

export default PageHeader;
